import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { catalogGateway } from '../../benefits-deductions';
import type { CatalogItemType, CompanyCatalogItem, CompanyCatalogSource } from '../../benefits-deductions/types';
import { useDebounced } from '../../benefits-deductions/useDebounced';

const PAGE_SIZE = 25;

export const CATALOG_HELPER_TEXT = 'Items created under People > Benefits and Deductions appear here.';

const noun = (type: CatalogItemType) => (type === 'Benefit' ? 'benefit' : 'deduction');

/** What to say when one half of the catalog could not be read: a missing list must never look like an empty company. */
function sourceProblem(type: CatalogItemType, sources: CompanyCatalogSource[]): string | null {
  const source = sources.find((s) => s.itemType === type);
  if (!source || source.status === 'Included') return null;
  return source.status === 'NoPermission'
    ? `You don’t have permission to view the company’s ${noun(type)}s.`
    : `The company’s ${noun(type)}s could not be loaded right now. Try again in a moment.`;
}

interface Props {
  type: CatalogItemType;
  value: CompanyCatalogItem | null;
  onChange: (item: CompanyCatalogItem) => void;
  disabled?: boolean;
  invalid?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  /** The id of the element that labels this field (the visible label). */
  labelledBy: string;
}

/**
 * Searchable, category-grouped, incrementally loaded picker over the active company catalog (GET /api/benefit-deduction-catalog). The server does the filtering, the
 * company scoping and the paging; nothing here knows a list of benefits or deductions. An item the server says cannot be assigned yet is shown, disabled, with the reason,
 * so the Admin can see why it is missing from the choices rather than wonder.
 */
export function CatalogPicker({ type, value, onChange, disabled, invalid, inputRef, labelledBy }: Props) {
  const listId = useId();
  const optionId = (index: number) => `${listId}-opt-${index}`;
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState<string | null>(null);
  const [items, setItems] = useState<CompanyCatalogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [sources, setSources] = useState<CompanyCatalogSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [moreBusy, setMoreBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(-1);
  /** How many inactive items of this type are not offered (a deduction waiting for tax review is saved inactive, so it is among them). */
  const [inactiveCount, setInactiveCount] = useState(0);
  const request = useRef(0);
  const container = useRef<HTMLDivElement>(null);

  const search = useDebounced((typed ?? '').trim(), 250);

  useEffect(() => {
    if (!open) return undefined;
    const mine = ++request.current;
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setFailed(false);
    catalogGateway
      .listItems({ type, search: search || undefined, status: 'active', page: 1, pageSize: PAGE_SIZE }, controller.signal)
      .then((page) => {
        if (request.current !== mine) return;
        setItems(page.items);
        setTotal(page.totalCount);
        setSources(page.sources);
        setActive(-1);
      })
      .catch(() => {
        if (request.current !== mine) return;
        setItems([]);
        setTotal(0);
        setFailed(true);
      })
      .finally(() => {
        if (request.current === mine) setLoading(false);
      });
    if (!search) {
      // A person who just made an item and cannot find it needs to know it is not lost: say how many inactive ones are left out. A failure here is silent.
      catalogGateway
        .listItems({ type, status: 'inactive', page: 1, pageSize: 1 }, controller.signal)
        .then((page) => { if (request.current === mine) setInactiveCount(page.totalCount); })
        .catch(() => { if (request.current === mine) setInactiveCount(0); });
    }
    return () => controller.abort();
  }, [open, type, search]);

  // The list closes when the person clicks somewhere else, and when they Tab away: not when the field loses focus. Closing on blur happens between the press and the
  // release of a click, and because the dialog is centred in the window the shrinking list moves everything under the pointer, so the click lands on the wrong thing.
  useEffect(() => {
    if (!open) return undefined;
    const onDocumentClick = (e: MouseEvent) => {
      if (container.current && !container.current.contains(e.target as Node)) {
        setOpen(false);
        setTyped(null);
      }
    };
    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, [open]);

  async function showMore() {
    if (moreBusy) return;
    const mine = request.current;
    setMoreBusy(true);
    try {
      const page = await catalogGateway.listItems({ type, search: search || undefined, status: 'active', page: Math.floor(items.length / PAGE_SIZE) + 1, pageSize: PAGE_SIZE });
      if (request.current !== mine) return;
      setItems((current) => {
        const known = new Set(current.map((i) => i.key));
        return [...current, ...page.items.filter((i) => !known.has(i.key))];
      });
      setTotal(page.totalCount);
    } catch {
      if (request.current === mine) setFailed(true);
    } finally {
      setMoreBusy(false);
    }
  }

  function choose(item: CompanyCatalogItem) {
    if (!item.assignable) return;
    onChange(item);
    setOpen(false);
    setTyped(null);
  }

  /** The next option the keyboard can land on: disabled options are skipped. */
  function step(from: number, direction: 1 | -1): number {
    for (let i = from + direction; i >= 0 && i < items.length; i += direction) {
      if (items[i]!.assignable) return i;
    }
    return from;
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActive((a) => step(a, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a <= 0 ? a : step(a, -1)));
    } else if (e.key === 'Home' && open) {
      e.preventDefault();
      setActive(step(-1, 1));
    } else if (e.key === 'End' && open) {
      e.preventDefault();
      setActive(step(items.length, -1));
    } else if (e.key === 'Enter' && open && active >= 0 && items[active]) {
      e.preventDefault();
      choose(items[active]!);
    } else if (e.key === 'Tab' && open) {
      setOpen(false);
      setTyped(null);
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      setTyped(null);
    }
  }

  const problem = sourceProblem(type, sources);
  const shownText = typed ?? value?.name ?? '';

  return (
    <div className="bd-picker" ref={container}>
      <input
        ref={inputRef}
        role="combobox"
        aria-labelledby={labelledBy}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        aria-required="true"
        aria-invalid={invalid ? true : undefined}
        autoComplete="off"
        placeholder={`Search ${noun(type)}s by name or code`}
        value={shownText}
        disabled={disabled}
        // The list opens on a deliberate action (a click, typing, or the arrow key), not when the field merely takes focus: the dialog focuses this field as it opens,
        // and a list that opens by itself closes again on the next click elsewhere, shifting the page under the pointer so that the click misses.
        onClick={() => setOpen(true)}
        onChange={(e) => {
          setTyped(e.target.value);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />
      {open && (
        <div className="bd-picker__panel">
          <ul id={listId} role="listbox" aria-label={`Company ${noun(type)}s`} className="bd-picker__list">
            {items.map((item, index) => {
              const header = index === 0 || items[index - 1]!.categoryName !== item.categoryName;
              return (
                <li key={item.key} role="presentation">
                  {header && <div className="bd-picker__group" role="presentation">{item.categoryName}</div>}
                  <div
                    id={optionId(index)}
                    role="option"
                    aria-selected={value?.key === item.key}
                    aria-disabled={!item.assignable || undefined}
                    className={`bd-picker__option${index === active ? ' bd-picker__option--active' : ''}${item.assignable ? '' : ' bd-picker__option--disabled'}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choose(item)}
                  >
                    <span className="bd-picker__name">{item.name}</span>
                    {item.shortCode && <span className="bd-picker__code muted">{item.shortCode}</span>}
                    {!item.assignable && <span className="bd-picker__why muted">{item.notAssignableReason ?? 'Not available yet'}</span>}
                  </div>
                </li>
              );
            })}
          </ul>
          {loading && <p className="bd-picker__status muted" role="status">Loading…</p>}
          {!loading && failed && <p className="bd-picker__status error" role="alert">The catalog could not be loaded. Try again in a moment.</p>}
          {!loading && !failed && problem && <p className="bd-picker__status error" role="alert">{problem}</p>}
          {!loading && !failed && !problem && items.length === 0 && (
            <p className="bd-picker__status muted" role="status">
              {search ? `No ${noun(type)} matches “${search}”.` : `No active ${noun(type)}s yet.`} {CATALOG_HELPER_TEXT}
            </p>
          )}
          {!loading && !failed && !search && inactiveCount > 0 && (
            <p className="bd-picker__status muted" role="note" data-testid="picker-inactive-note">
              {inactiveCount} inactive {noun(type)}{inactiveCount === 1 ? ' is' : 's are'} not offered here, including any waiting for tax review. They are listed under People &gt; Benefits and Deductions.
            </p>
          )}
          {!loading && items.length < total && (
            <button type="button" className="bd-picker__more" disabled={moreBusy} onMouseDown={(e) => e.preventDefault()} onClick={() => void showMore()}>
              {moreBusy ? 'Loading…' : `Show more (${total - items.length} more)`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
